import { useEffect, useState } from 'react';

import type { RuleConfigDto } from '../../lib/api/endpoints';
import { Button } from '../ui/Button';
import { Input } from '../ui/Input';

interface RuleConfigRowProps {
  rule: RuleConfigDto;
  isSaving?: boolean;
  onSave: (input: {
    value: number | boolean;
    enabled: boolean;
    severity: 'INFO' | 'WARN' | 'BLOCK';
    overridable: boolean;
  }) => void;
}

export function RuleConfigRow({ rule, isSaving = false, onSave }: RuleConfigRowProps) {
  const [value, setValue] = useState(String(rule.value));
  const [enabled, setEnabled] = useState(rule.enabled);
  const [severity, setSeverity] = useState(rule.severity);
  const [overridable, setOverridable] = useState(rule.overridable);

  useEffect(() => {
    setValue(String(rule.value));
    setEnabled(rule.enabled);
    setSeverity(rule.severity);
    setOverridable(rule.overridable);
  }, [rule]);

  const parsedValue = typeof rule.value === 'boolean' ? value === 'true' : Number(value);
  const dirty =
    value !== String(rule.value) ||
    enabled !== rule.enabled ||
    severity !== rule.severity ||
    overridable !== rule.overridable;

  return (
    <div className="grid gap-3 border-b border-border py-4 lg:grid-cols-[1fr_140px_120px_120px_110px] lg:items-center">
      <div>
        <p className="font-mono text-sm">{rule.ruleKey}</p>
        <p className="text-xs text-text-muted">{rule.notes}</p>
      </div>
      {typeof rule.value === 'boolean' ? (
        <select
          className="h-10 rounded border border-border bg-bg px-3 text-sm"
          aria-label={`${rule.ruleKey} value`}
          value={value}
          onChange={(event) => setValue(event.target.value)}
        >
          <option value="true">true</option>
          <option value="false">false</option>
        </select>
      ) : (
        <Input
          aria-label={`${rule.ruleKey} threshold`}
          value={value}
          onChange={(event) => setValue(event.target.value)}
        />
      )}
      <select
        className="h-10 rounded border border-border bg-bg px-3 text-sm"
        aria-label={`${rule.ruleKey} severity`}
        value={severity}
        onChange={(event) => setSeverity(event.target.value as 'INFO' | 'WARN' | 'BLOCK')}
      >
        <option value="INFO">INFO</option>
        <option value="WARN">WARN</option>
        <option value="BLOCK">BLOCK</option>
      </select>
      <label className="flex items-center gap-2 text-sm">
        <input
          type="checkbox"
          checked={enabled}
          onChange={(event) => setEnabled(event.target.checked)}
        />
        Enabled
      </label>
      <label className="flex items-center gap-2 text-sm">
        <input
          type="checkbox"
          checked={overridable}
          onChange={(event) => setOverridable(event.target.checked)}
        />
        Override
      </label>
      <Button
        type="button"
        size="sm"
        variant={dirty ? 'primary' : 'secondary'}
        disabled={!dirty || isSaving}
        onClick={() => onSave({ value: parsedValue, enabled, severity, overridable })}
      >
        Save
      </Button>
    </div>
  );
}
